import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Star, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    rating: 0,
    comment: '',
    referenceCode: '',
    location: ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/reviews/submit`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        rating: formData.rating,
        comment: formData.comment,
        reference_code: formData.referenceCode,
        location: formData.location || null
      });

      setSubmitStatus({
        type: 'success',
        message: '🎉 Merci ! Votre avis a été envoyé avec succès. Il sera publié sur notre site internet après validation par notre équipe (sous 24-48h).'
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        rating: 0,
        comment: '',
        referenceCode: '',
        location: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating || formData.rating;

  return (
    <div className="mt-16">
      {/* Info Section */}
      <div className="max-w-3xl mx-auto mb-8 bg-[#c9a961]/10 border-2 border-[#c9a961]/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#c9a961] rounded-full flex items-center justify-center flex-shrink-0">
            <Star className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0B1F3C] mb-2">
              Client Domus & Co ? Publiez votre avis !
            </h3>
            <p className="text-gray-700 mb-3">
              Votre satisfaction est notre priorité. Partagez votre expérience pour aider d'autres clients à faire le bon choix.
            </p>
            <div className="bg-white rounded-lg p-4 border border-[#c9a961]/20">
              <p className="text-sm font-semibold text-[#0B1F3C] mb-2">
                📋 Comment publier votre avis :
              </p>
              <ol className="text-sm text-gray-700 space-y-1 ml-4 list-decimal">
                <li>Remplissez le formulaire ci-dessous avec vos informations</li>
                <li>Utilisez le <strong>code de référence</strong> que nous vous avons fourni à la fin de votre projet</li>
                <li>Notre équipe validera votre avis sous 24-48h</li>
                <li>Votre témoignage sera publié sur cette page</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-xl max-w-3xl mx-auto">
        <CardHeader className="bg-gradient-to-r from-[#0B1F3C] to-[#1a3a52] text-white rounded-t-lg">
          <CardTitle className="text-2xl">Formulaire d'avis client</CardTitle>
          <p className="text-white/90 text-sm mt-2">
            Partagez votre expérience de rénovation avec Domus & Co
          </p>
        </CardHeader>
        <CardContent className="p-8">
        {submitStatus && (
          <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            submitStatus.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {submitStatus.type === 'success' ? (
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            ) : (
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            )}
            <p className={submitStatus.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {submitStatus.message}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-[#0B1F3C] font-medium">
                Prénom *
              </Label>
              <Input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Jean"
                className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-[#0B1F3C] font-medium">
                Nom *
              </Label>
              <Input
                id="lastName"
                name="lastName"
                data-testid="review-lastName-input"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Dupont"
                className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[#0B1F3C] font-medium">
              Votre note *
            </Label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={32}
                    className={
                      star <= displayRating
                        ? 'fill-[#c9a961] text-[#c9a961]'
                        : 'text-gray-300'
                    }
                  />
                </button>
              ))}
              {formData.rating > 0 && (
                <span className="ml-2 text-[#0B1F3C] font-semibold self-center">
                  {formData.rating}/5
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location" className="text-[#0B1F3C] font-medium">
              Localisation (optionnel)
            </Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Paris 16ème"
              className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="referenceCode" className="text-[#0B1F3C] font-medium">
              Code de référence projet *
            </Label>
            <Input
              id="referenceCode"
              name="referenceCode"
              value={formData.referenceCode}
              onChange={handleChange}
              required
              placeholder="Ex: DC-2024-001"
              className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
              data-testid="review-reference-code"
            />
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
              <p className="text-sm text-blue-800">
                <strong>📧 Code introuvable ?</strong> Ce code unique vous a été fourni par email ou SMS à la fin de votre projet. 
                Si vous ne le trouvez pas, contactez-nous au <strong>06 66 77 01 27</strong> ou par email à <strong>concept.domus.co@gmail.com</strong>
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment" className="text-[#0B1F3C] font-medium">
              Votre avis *
            </Label>
            <Textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Partagez votre expérience avec Domus & Co..."
              className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
            />
          </div>

          <Button
            type="submit"
            data-testid="review-submit-button"
            disabled={isSubmitting || formData.rating === 0}
            className="w-full bg-[#c9a961] hover:bg-[#b89951] text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              'Envoi en cours...'
            ) : (
              <>
                Publier mon avis
                <Send className="ml-2" size={20} />
              </>
            )}
          </Button>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-700">
              ✅ Votre avis sera <strong>publié sur notre site internet</strong> après validation par notre équipe (sous 24-48h)
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
    </div>
  );
};

export default ReviewForm;
